import { action, computed, observable } from 'mobx'
import { RynoSymbol, SymbolKind } from '@taxfyle/ryno'
import { func, type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import type { ErrorDescriptor } from '@/features/legends/editor/analyzer-errors'
import { newCid } from '@/features/legends/editor/collections'
import type { AnswerOption } from '@/features/legends/editor/entities'
import { UnknownElementError } from '@/features/legends/editor/legend-document'
import { Types } from '@/features/legends/editor/ryno-types'
import type { QuestionEditorViewModelBase } from '@/features/legends/questions/QuestionEditorViewModelBase'
import { RynoEditorViewModel } from '@/features/legends/ryno/RynoEditorViewModel'
import type { TemplateEditorViewModel } from './TemplateEditorViewModel'
import {
  type TemplateMapping,
  type TemplateMappingKind,
  templateDataSources,
} from './templates'

export type MappingType = Exclude<TemplateMappingKind['type'], 'Unknown'>

/**
 * How a template answers one question when scoping a job: with a formula over
 * the template's data sources, or by asking.
 */
export class MappingEditorViewModel extends LegendPartViewModelBase {
  private readonly _templateVm: TemplateEditorViewModel
  private readonly _schema: IValidationSchema<MappingEditorViewModel> = {
    expressionVm: [
      func(() => this.expressionVm.analyze(), 'There are formula errors.'),
    ],
  }

  readonly cid = newCid('mapping')

  /**
   * The question this mapping answers.
   */
  readonly questionVm: QuestionEditorViewModelBase

  /**
   * The formula that answers the question, when the type is `Expr`.
   */
  readonly expressionVm: RynoEditorViewModel

  @observable accessor id = ''
  @observable accessor type: MappingType = 'Expr'

  readonly validation = validationContext(this, this._schema)

  constructor(
    dependencies: LegendPartDependencies,
    templateVm: TemplateEditorViewModel,
    questionVm: QuestionEditorViewModelBase,
  ) {
    super(dependencies)
    this._templateVm = templateVm
    this.questionVm = questionVm
    this.expressionVm = new RynoEditorViewModel({
      required: () => this.type === 'Expr',
      getSymbolTable: () => this._symbolTable,
      getNeededType: () => this.questionVm.valueType,
    })
    this.lifecycle.manage(this.expressionVm)
  }

  /**
   * The ID of the question this mapping answers.
   */
  @computed
  get questionId() {
    return this.questionVm.id
  }

  /**
   * The question's description, for questions that have one.
   */
  @computed
  get questionDescription(): string {
    return 'description' in this.questionVm &&
      typeof this.questionVm.description === 'string'
      ? this.questionVm.description
      : ''
  }

  /**
   * The answers the formula can return, for questions with a fixed set.
   */
  @computed
  get possibleReturnValues(): AnswerOption[] {
    const options = this.questionVm.answerOptions
    return options && options.type === 'select' ? options.options : []
  }

  /**
   * The legend's symbols, plus the template's data sources.
   */
  @computed
  private get _symbolTable() {
    const table = this.dependencies.symbols.getSymbolTableFor(this)
    for (const source of templateDataSources) {
      table.setIdentifierSymbol(
        new RynoSymbol(
          source.id,
          Types.fromJS(source.valueType),
          SymbolKind.Identifier,
          true,
        ),
      )
    }
    return table
  }

  @computed
  private get _json(): TemplateMapping {
    return {
      id:
        this.id ||
        this.dependencies.symbols.newId(
          this.cid,
          `${this._templateVm.id}_MAPPING_${this.questionId}`,
        ),
      questionId: this.questionId,
      kind:
        this.type === 'Expr'
          ? { type: 'Expr', expr: this.expressionVm.value }
          : { type: 'Manual' },
    }
  }

  /**
   * Sets how the question is answered.
   */
  @action.bound
  setType(type: MappingType) {
    this.validation.reset()
    this.type = type
  }

  /**
   * Sets the mapping from the legend document. Throws for a kind the builder
   * can't edit.
   */
  @action
  hydrate(mapping: TemplateMapping) {
    this.id = mapping.id ?? ''
    const { kind } = mapping
    switch (kind.type) {
      case 'Expr':
        this.type = 'Expr'
        this.expressionVm.setValue(kind.expr)
        break
      case 'Manual':
        this.type = 'Manual'
        break
      case 'Unknown':
        throw new UnknownElementError('template mapping', kind)
    }
  }

  /**
   * Shows the analyzer's errors for the formula.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    const exprErrors = descriptor.inner?.kind?.inner?.expr
    if (exprErrors) {
      this.validation.addErrors({ expressionVm: exprErrors.errors })
    }
  }

  /**
   * The mapping for the legend document.
   */
  toJSON(): TemplateMapping {
    return this._json
  }
}
